import Swiper from "swiper";
import { ScrollLock } from "../features/ScrollLock.js";

/**
 * Shared image gallery. Mark a container with data-lightbox-gallery and its
 * full-resolution image links with data-lightbox-item. An external button can
 * open it via data-lightbox-open="container-id". Each container is one gallery.
 */
export class Lightbox
{
    private dialog: HTMLDialogElement;
    private stage: HTMLElement;
    private caption: HTMLElement;
    private previous: HTMLButtonElement;
    private next: HTMLButtonElement;
    private closer: HTMLButtonElement;
    private items: HTMLAnchorElement[] = [];
    private index = 0;
    private opener?: HTMLElement;
    private scrollLock = ScrollLock.use();
    private suppressBackdropClickUntil = 0;
    private slider?: Swiper;

    public constructor()
    {
        this.dialog = document.createElement("dialog");
        this.dialog.className = "image-lightbox";
        this.dialog.setAttribute("aria-label", "Bildergalerie");
        this.dialog.innerHTML = `
            <button type="button" class="image-lightbox__close" aria-label="Bildergalerie schließen (Escape)">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-x" aria-hidden="true" focusable="false"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg>
            </button>
            <div class="image-lightbox__stage swiper"><div class="swiper-wrapper"></div></div>
            <p class="image-lightbox__caption" aria-live="polite" aria-atomic="true"></p>
            <div class="image-lightbox__footer" role="group" aria-label="Bildnavigation">
                <button type="button" class="image-lightbox__previous" aria-label="Vorheriges Bild (Pfeil links)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-chevron-left" aria-hidden="true" focusable="false"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M15 6l-6 6l6 6" /></svg>
                </button>
                <button type="button" class="image-lightbox__next" aria-label="Nächstes Bild (Pfeil rechts)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-chevron-right" aria-hidden="true" focusable="false"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M9 6l6 6l-6 6" /></svg>
                </button>
            </div>`;
        this.stage = this.dialog.querySelector(".image-lightbox__stage")!;
        this.caption = this.dialog.querySelector(".image-lightbox__caption")!;
        this.previous = this.dialog.querySelector(".image-lightbox__previous")!;
        this.next = this.dialog.querySelector(".image-lightbox__next")!;
        this.closer = this.dialog.querySelector(".image-lightbox__close")!;
        document.body.append(this.dialog);

        document.addEventListener("click", this.onClick);
        this.closer.addEventListener("click", () => this.dialog.close());
        this.previous.addEventListener("click", () => this.show(this.index - 1));
        this.next.addEventListener("click", () => this.show(this.index + 1));
        this.dialog.addEventListener("keydown", this.onKeydown);
        this.dialog.addEventListener("click", event => {
            if (performance.now() < this.suppressBackdropClickUntil) return;
            if (event.target === this.dialog || event.target === this.stage ||
                (event.target instanceof Element && event.target.classList.contains("swiper-slide"))) {
                this.dialog.close();
            }
        });
        this.dialog.addEventListener("close", () => {
            this.slider?.destroy(true, true);
            this.slider = undefined;
            this.stage.querySelector(".swiper-wrapper")!.replaceChildren();
            this.scrollLock.unlock();
            this.opener?.focus({ preventScroll: true });
        });
        new ResizeObserver(() => this.fitImages()).observe(this.stage);
    }

    private onClick = (event: MouseEvent): void => {
        if (!(event.target instanceof Element) || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const trigger = event.target.closest<HTMLElement>("[data-lightbox-open], [data-lightbox-item]");
        if (!trigger) return;
        const gallery = trigger.hasAttribute("data-lightbox-open")
            ? document.getElementById(trigger.dataset.lightboxOpen!)
            : trigger.closest<HTMLElement>("[data-lightbox-gallery]");
        if (!gallery) return;
        const items = Array.from(gallery.querySelectorAll<HTMLAnchorElement>("a[data-lightbox-item]"));
        if (!items.length) return;
        event.preventDefault();
        this.items = items;
        this.opener = trigger;
        this.suppressBackdropClickUntil = 0;
        this.dialog.showModal();
        this.scrollLock.lock();
        this.createSlider(Math.max(0, items.indexOf(trigger as HTMLAnchorElement)));
        this.closer.focus();
    };

    private createSlider(index: number): void
    {
        const wrapper = this.stage.querySelector<HTMLElement>(".swiper-wrapper")!;
        wrapper.replaceChildren();
        this.items.forEach((item, slideIndex) => {
            const slide = document.createElement("div");
            slide.className = "swiper-slide";
            slide.setAttribute("role", "group");
            slide.setAttribute("aria-label", `Bild ${slideIndex + 1} von ${this.items.length}`);
            const image = document.createElement("img");
            image.className = "image-lightbox__image";
            image.alt = item.dataset.alt || item.dataset.caption || "";
            image.draggable = false;
            image.loading = "lazy";
            image.style.visibility = "hidden";
            image.addEventListener("load", () => this.fitImages());
            image.addEventListener("error", () => {
                slide.dataset.error = "true";
                if (slideIndex === this.index) this.updateSlide(this.index);
            });
            image.src = item.href;
            slide.append(image);
            wrapper.append(slide);
        });
        const reducedMotion = typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        this.slider = new Swiper(this.stage, {
            initialSlide: index,
            slidesPerView: 1,
            spaceBetween: 24,
            speed: reducedMotion ? 0 : 260,
            threshold: 8,
            longSwipesRatio: 0.15,
            grabCursor: true,
            watchOverflow: true,
            // Keep focus on the dialog's buttons when a drag starts on an image.
            focusableElements: "input, select, textarea",
            on: {
                slideChange: slider => this.updateSlide(slider.activeIndex),
                sliderFirstMove: () => { this.suppressBackdropClickUntil = Infinity; },
                touchEnd: () => {
                    if (this.suppressBackdropClickUntil === Infinity) {
                        this.suppressBackdropClickUntil = performance.now() + 400;
                    }
                },
            },
        });
        this.updateSlide(index);
    }

    private show(index: number): void
    {
        if (index < 0 || index >= this.items.length || index === this.index) return;
        this.slider?.slideTo(index);
    }

    private updateSlide(index: number): void
    {
        this.index = index;
        const caption = this.items[index]?.dataset.caption || "";
        const slides = this.stage.querySelectorAll<HTMLElement>(".swiper-slide");
        slides.forEach((slide, slideIndex) => {
            slide.setAttribute("aria-hidden", String(slideIndex !== index));
        });
        const error = slides[index]?.dataset.error ? "Das Bild konnte nicht geladen werden. " : "";
        this.caption.textContent = `${error}Bild ${index + 1} von ${this.items.length}${caption ? ` – ${caption}` : ""}`;
        // Keep controls focusable at the edges so focus never disappears.
        this.previous.hidden = this.next.hidden = this.items.length < 2;
        this.previous.setAttribute("aria-disabled", String(index === 0));
        this.next.setAttribute("aria-disabled", String(index === this.items.length - 1));
        this.fitImages();
    }

    private fitImages(): void
    {
        if (!this.dialog.open) return;
        const bounds = this.stage.getBoundingClientRect();
        this.stage.querySelectorAll<HTMLImageElement>("img").forEach(image => {
            if (!image.naturalWidth) return;
            // Original pixels are never enlarged, including on high-density screens.
            const vector = /\.svg(?:[?#]|$)/i.test(image.src);
            const scale = Math.min(bounds.width / image.naturalWidth, bounds.height / image.naturalHeight,
                vector ? Infinity : 1 / (window.devicePixelRatio || 1));
            image.style.width = `${image.naturalWidth * scale}px`;
            image.style.height = `${image.naturalHeight * scale}px`;
            image.style.visibility = "visible";
        });
    }

    private onKeydown = (event: KeyboardEvent): void => {
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            this.show(this.index + (event.key === "ArrowLeft" ? -1 : 1));
        }
        if (event.key === "Home" || event.key === "End") {
            event.preventDefault();
            this.show(event.key === "Home" ? 0 : this.items.length - 1);
        }
        if (event.key === "Tab") {
            const last = this.next.hidden ? this.closer : this.next;
            if (event.shiftKey && document.activeElement === this.closer) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                this.closer.focus();
            }
        }
    };
}
